import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { BehaviorSubject, Observable } from 'rxjs';
import { GlobalTenantContractsApi, TenantContractModel, TenantContractModelPagedList } from 'src/app/usage-api';
import { BaseDataSource, TableColumn } from './datasource';

export interface FindContractsRequest {
  page?: number;
  pageSize?: number;
  sort?: string;
  search?: string;
  tenantId?: string;
  brand?: string;
  isActive?: boolean;
}

export class ContractsDataSource extends BaseDataSource<TenantContractModel, GlobalTenantContractsApi, FindContractsRequest> {
  columns: TableColumn<TenantContractModel>[] = [
    {
      field: 'tenantName',
      localizationKey: 'BackOffice.ManageContracts.Contract.TenantName',
      link: (val: TenantContractModel) => {
        return {
          route: ['/back-office/contracts', val.tenantId, val.id],
          queryParams: null,
        };
      },
      style: { 'max-width': '12rem', 'min-width': '12rem', width: '12rem' },
      sticky: true,
      noSort: true,
    },
    {
      field: 'tenantId',
      localizationKey: 'BackOffice.ManageContracts.Contract.TenantId',
      style: { 'max-width': '8rem', 'min-width': '8rem', width: '8rem' },
      noSort: true,
    },
    {
      field: 'brand',
      localizationKey: 'BackOffice.ManageContracts.Contract.Brand',
      style: { 'max-width': '8rem', 'min-width': '8rem', width: '8rem' },
      noSort: true,
    },
    {
      field: 'contractStartDate',
      localizationKey: 'BackOffice.ManageContracts.Contract.ContractStartDate',
      render: (val) => {
        if (val.contractStartDate) {
          const date = new Date(val.contractStartDate);
          return date.toLocaleDateString();
        }
        return '';
      },
      style: { 'max-width': '10rem', 'min-width': '10rem', width: '10rem' },
      noSort: true,
    },
    {
      field: 'servicePeriodEndDate',
      localizationKey: 'BackOffice.ManageContracts.Contract.ServiceEndDate',
      render: (val) => {
        if (val.servicePeriodEndDate) {
          const date = new Date(val.servicePeriodEndDate);
          return date.toLocaleDateString();
        }
        return '';
      },
      style: { 'max-width': '10rem', 'min-width': '10rem', width: '10rem' },
      noSort: true,
    },
    {
      field: 'isActive',
      localizationKey: 'BackOffice.ManageContracts.Contract.Active',
      render: (val) => {
        return val.isActive ? 'Active' : 'Not Active';
      },
      style: { 'max-width': '6rem', 'min-width': '6rem', width: '6rem' },
      noSort: true,
    },
  ];

  get availableColumns(): TableColumn<TenantContractModel>[] {
    return this.columns;
  }

  get queryArguments(): object {
    const request = this.currentRequest.value;
    return {
      page: request.page,
      pageSize: request.pageSize,
      sort: request.sort ? request.sort : null,
      search: request.search ? request.search : null,
      tenantId: request.tenantId,
      brand: request.brand,
      isActive: request.isActive,
    };
  }

  brandSearchTerm$ = new BehaviorSubject<string>(null);
  isActiveSearchTerm$ = new BehaviorSubject<boolean>(null);

  previousRequest: FindContractsRequest;

  constructor(service: GlobalTenantContractsApi, paginator: MatPaginator, sort: MatSort, request: FindContractsRequest, loadOnInit: boolean = true) {
    super(service, paginator, sort, request, loadOnInit);
  }

  override init(setInit: boolean = false) {
    super.init(setInit);

    this.subscriptions.add(this.createSubscription(this.brandSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.isActiveSearchTerm$));

    this.hasInit = true;
  }

  public override loadData(forceRefresh: boolean = false) {
    if (!this.hasInit) {
      return;
    }

    const currentRequest = this.currentRequest.value ?? {};

    currentRequest.sort = this.sortDirection + this.sortField;
    currentRequest.page = this.pageIndex + 1;
    currentRequest.pageSize = this.pageSize;
    currentRequest.search = this.search;
    currentRequest.brand = this.brandSearchTerm$.value;
    currentRequest.isActive = this.isActiveSearchTerm$.value;

    if (this.previousRequest && !forceRefresh && this.isEqual(this.previousRequest, currentRequest)) {
      return;
    }

    this.currentRequest.next(currentRequest);
    this.loadingSubject.next(true);
    this.hasError.next(false);
    this.errorMessage.next(null);

    this.previousRequest = { ...currentRequest };

    const request$: Observable<TenantContractModelPagedList> = this.service.globalTenantContractsFindContracts(
      currentRequest.isActive,
      null,
      currentRequest.page,
      currentRequest.pageSize
    );

    this.handleResponse(request$, 'BackOffice.ManageContracts.Table.ErrorMessage').subscribe();
 }

  isEqual(a: FindContractsRequest, b: FindContractsRequest): boolean {
    return (
      a.page == b.page &&
      a.pageSize == b.pageSize &&
      a.sort == b.sort &&
      a.search == b.search &&
      a.tenantId == b.tenantId &&
      a.brand == b.brand &&
      a.isActive == b.isActive
    );
  }
}